/**
 * Hooks react-query para os endpoints de observabilidade do WhatsApi.
 *
 * Polling diferenciado:
 *  - overview/health: 30s (stale 15s) — metricas de alto nivel e heartbeat
 *  - throughput/byApiKey/errors: 60s (stale 30s) — series temporais e agregados
 *
 * Hooks aceitam opts.escopo opcional (useEscopoWhatsApi): tenant usa
 * /v1/tenants/{id}/whatsapi/stats/*, organização usa
 * /v1/organizations/{id}/whatsapi/stats/*, e sem escopo (ou `nenhum`) o painel
 * global /v1/whatsapi-proxy/stats/*. O queryKey isola os três caches (tenantId,
 * `org:{id}` ou 'global'). A organização não tem rota de saúde: useHealth não dispara.
 *
 * Tratamento de 424 no_api_keys: queryFn envolvida em try/catch,
 * response 424+detail='no_api_keys' retorna marker NO_API_KEYS (string property).
 * Consumidores usam isNoApiKeys(data) para renderizar empty state.
 *
 * retry=3 com backoff exponencial (exceto 424 — no_api_keys ou whatsapi_disabled — nao retentar).
 */

import { useQuery } from '@tanstack/react-query'
import {
  whatsapiStatsApi,
  whatsapiTenantStatsApi,
  type Bucket,
  type ByApiKey,
  type ErrorsResponse,
  type EstatisticasWhatsApi,
  type Health,
  type Overview,
  type Period,
  type Throughput,
} from '@/api/whatsapiStats'
import { whatsapiOrgApi } from '@/api/whatsapiOrg'
import type { EscopoWhatsApi } from '@/hooks/useEscopoWhatsApi'

export const ROOT_KEY = 'whatsapi-stats' as const

// ── NO_API_KEYS sentinel ────────────────────────────────────────────────────
// String property marker (em vez de Symbol.for) para sobreviver a JSON.stringify
// usado pelo PersistQueryClientProvider (main.tsx). Namespacing via prefixo
// __whatsapi para zero colisão com payloads reais.
export type NoApiKeysMarker = { readonly __whatsapiNoApiKeys: true }

export function isNoApiKeys(data: unknown): data is NoApiKeysMarker {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { __whatsapiNoApiKeys?: boolean }).__whatsapiNoApiKeys === true
  )
}

const NO_API_KEYS_MARKER: NoApiKeysMarker = Object.freeze({ __whatsapiNoApiKeys: true })

/** `detail` de uma resposta 424 da API (`no_api_keys`, `whatsapi_disabled`), ou null. */
export function detalheDo424(err: unknown): string | null {
  if (!err || typeof err !== 'object' || !('response' in err)) return null
  const resp = err.response
  if (!resp || typeof resp !== 'object' || !('status' in resp) || resp.status !== 424) return null
  if (!('data' in resp) || !resp.data || typeof resp.data !== 'object' || !('detail' in resp.data)) return ''
  return typeof resp.data.detail === 'string' ? resp.data.detail : ''
}

function is424NoApiKeys(err: unknown): boolean {
  return detalheDo424(err) === 'no_api_keys'
}

/** 424 e estado de configuracao (sem chave, WhatsApi desligado): repetir nao muda nada. */
function retryExcept424(count: number, err: unknown): boolean {
  if (detalheDo424(err) !== null) return false
  return count < 3
}

function statsApiFor(escopo?: EscopoWhatsApi): EstatisticasWhatsApi {
  if (escopo?.tipo === 'tenant') return whatsapiTenantStatsApi(escopo.tenantId)
  if (escopo?.tipo === 'organizacao') return whatsapiOrgApi.stats(escopo.orgId)
  return whatsapiStatsApi
}

/** Parte do queryKey que separa os caches; o tenant segue com o id puro de antes. */
function chaveDoEscopo(escopo?: EscopoWhatsApi): number | string {
  if (escopo?.tipo === 'tenant') return escopo.tenantId
  if (escopo?.tipo === 'organizacao') return `org:${escopo.orgId}`
  return 'global'
}

// ── Hooks ───────────────────────────────────────────────────────────────────

interface EscopoOpts {
  escopo?: EscopoWhatsApi
}

export function useOverview(period: Period, opts?: EscopoOpts) {
  return useQuery<Overview | NoApiKeysMarker>({
    queryKey: [ROOT_KEY, 'overview', period, chaveDoEscopo(opts?.escopo)],
    queryFn: async () => {
      try {
        return await statsApiFor(opts?.escopo).overview(period)
      } catch (err) {
        if (is424NoApiKeys(err)) return NO_API_KEYS_MARKER
        throw err
      }
    },
    refetchInterval: 30_000,
    staleTime: 15_000,
    retry: retryExcept424,
  })
}

export function useThroughput(period: Period, bucket: Bucket, opts?: EscopoOpts) {
  return useQuery<Throughput | NoApiKeysMarker>({
    queryKey: [ROOT_KEY, 'throughput', period, bucket, chaveDoEscopo(opts?.escopo)],
    queryFn: async () => {
      try {
        return await statsApiFor(opts?.escopo).throughput(period, bucket)
      } catch (err) {
        if (is424NoApiKeys(err)) return NO_API_KEYS_MARKER
        throw err
      }
    },
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: retryExcept424,
  })
}

export function useByApiKey(period: Period, opts?: EscopoOpts) {
  return useQuery<ByApiKey | NoApiKeysMarker>({
    queryKey: [ROOT_KEY, 'by-api-key', period, chaveDoEscopo(opts?.escopo)],
    queryFn: async () => {
      try {
        return await statsApiFor(opts?.escopo).byApiKey(period)
      } catch (err) {
        if (is424NoApiKeys(err)) return NO_API_KEYS_MARKER
        throw err
      }
    },
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: retryExcept424,
  })
}

export function useErrors(period: Period, limit = 20, opts?: EscopoOpts) {
  return useQuery<ErrorsResponse | NoApiKeysMarker>({
    queryKey: [ROOT_KEY, 'errors', period, limit, chaveDoEscopo(opts?.escopo)],
    queryFn: async () => {
      try {
        return await statsApiFor(opts?.escopo).errors(period, limit)
      } catch (err) {
        if (is424NoApiKeys(err)) return NO_API_KEYS_MARKER
        throw err
      }
    },
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: retryExcept424,
  })
}

export function useHealth(opts?: EscopoOpts) {
  const escopo = opts?.escopo
  return useQuery<Health | NoApiKeysMarker>({
    queryKey: [ROOT_KEY, 'health', chaveDoEscopo(escopo)],
    queryFn: async () => {
      try {
        return escopo?.tipo === 'tenant'
          ? await whatsapiTenantStatsApi(escopo.tenantId).health()
          : await whatsapiStatsApi.health()
      } catch (err) {
        if (is424NoApiKeys(err)) return NO_API_KEYS_MARKER
        throw err
      }
    },
    enabled: escopo?.tipo !== 'organizacao',
    refetchInterval: 30_000,
    staleTime: 15_000,
    retry: retryExcept424,
  })
}
